import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'react-use';

import { BuildingReadModelSort, SortOrder } from '@client/api';
import { DropDownSort, DropDownSortEntry, SearchField, Skeleton, useSearching, useSorting } from '@client/toolkit';

import { useBuildingsSearchQueryParams, useBuildingsSortQueryParams } from '../hooks/use-buildings-query-params';

const SORT_OPTIONS: DropDownSortEntry<BuildingReadModelSort, SortOrder>[] = [
  { property: 'Name', order: 'Asc', label: 'space.name' },
  { property: 'Name', order: 'Desc', label: 'space.name' },
  { property: 'TotalArea', order: 'Asc', label: 'building.totalArea' },
  { property: 'TotalArea', order: 'Desc', label: 'building.totalArea' },
];

interface BuildingsTableToolbarProps {
  totalCount?: number;
  isFetching?: boolean;
}

export function BuildingsTableToolbar({ totalCount, isFetching }: BuildingsTableToolbarProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const [searchQueryParams, setSearchQueryParam] = useBuildingsSearchQueryParams();
  const [sortQueryParams, setSortQueryParam] = useBuildingsSortQueryParams();
  const { search, clearSearch, debouncedSearch, updateSearch } = useSearching({
    search: searchQueryParams.buildingsSearch,
  });

  const { sortProperty, sortOrder, updateSortOrder, updateSortProperty } = useSorting<BuildingReadModelSort, SortOrder>(
    {
      sortProperty: sortQueryParams.buildingsSortProperty,
      sortOrder: sortQueryParams.buildingsSortOrder,
    },
  );

  useUpdateEffect(() => {
    setSearchQueryParam({
      buildingsSearch: debouncedSearch ?? undefined,
    });
  }, [debouncedSearch]);

  useUpdateEffect(() => {
    setSortQueryParam({
      buildingsSortOrder: sortOrder ?? undefined,
    });
  }, [sortOrder]);

  useUpdateEffect(() => {
    setSortQueryParam({
      buildingsSortProperty: sortProperty ?? undefined,
    });
  }, [sortProperty]);

  return (
    <div className="w-full my-4 px-4 flex items-center">
      <div className="flex-1">
        <div className="flex items-center">
          <SearchField value={search} onChange={updateSearch} onClear={clearSearch} placeholder={tCommon('search')} />
          <div className="text-white text-base font-regular ml-4">
            {isFetching && <Skeleton variant="text" width="w-32" />}
            {!isFetching && (
              <>
                <span className="font-medium mr-1">{totalCount}</span>
                <span>
                  {tGlobal('entity.building', {
                    count: totalCount ?? 0,
                    context: totalCount && totalCount > 1 ? 'plural' : 'singular',
                  })}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <div>
        <DropDownSort<BuildingReadModelSort, SortOrder>
          selected={{
            order: sortOrder,
            property: sortProperty,
          }}
          onSortChange={(entry) => {
            updateSortOrder(entry.order);
            updateSortProperty(entry.property);
          }}
          promptLabel={`${tCommon('sortBy')}:`}
          getSelectedLabel={(label) => (label ? tGlobal(label) : '')}
          getOptionLabel={(label) => (label ? tGlobal(label) : '')}
          ascLabel={tCommon('asc')}
          descLabel={tCommon('desc')}
          columns={SORT_OPTIONS}
        />
      </div>
    </div>
  );
}
